import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@shared/context/AuthContext";
import {
  FeatureAccessDeniedReason,
  type FeatureAvailabilityResult,
  toFeatureAccessDeniedReason,
  useFeatureFlags,
} from "@shared/hooks/useFeatureFlags";

type RegulatoryPhase = 1 | 2 | 3;

interface PermissionCheck {
  resource: string;
  action: string;
  deniedDetail?: string;
}

interface UsePhaseFeatureAccessGuardOptions {
  phase: RegulatoryPhase;
  featureKey: string;
  featureUnavailableDetail?: string;
  missingOrgDetail?: string;
  authRequiredDetail?: string;
  permissionCheck?: PermissionCheck;
}

interface UsePhaseFeatureAccessGuardResult {
  accessLoading: boolean;
  accessDenied: boolean;
  accessDetail: string;
  accessReason?: FeatureAccessDeniedReason;
  refreshAccess: () => Promise<void>;
  handleGuardedApiError: (error: unknown, fallbackDetail?: string) => boolean;
}

function defaultPhaseDeniedDetail(phase: RegulatoryPhase, featureKey: string): string {
  return `Regulatory Phase ${phase} is not enabled. Feature "${featureKey}" requires Phase ${phase} clearance.`;
}

function defaultFeatureDeniedDetail(featureKey: string): string {
  return `${featureKey.replace(/_/g, " ")} is not enabled for this organization.`;
}

const ORGANIZATION_UUID_IN_QUOTES_PATTERN =
  /organization\s+"[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}"/gi;

function getUserOrganizationName(
  user: { organization?: { name?: string | null } | null } | null | undefined,
): string | null {
  const name = user?.organization?.name;
  return typeof name === "string" && name.trim().length > 0
    ? name.trim()
    : null;
}

function sanitizeOrganizationDetail(
  detail: string,
  organizationName: string | null,
): string {
  if (!detail) return detail;
  return detail.replace(
    ORGANIZATION_UUID_IN_QUOTES_PATTERN,
    organizationName
      ? `organization "${organizationName}"`
      : "your organization",
  );
}

function detailFromDeniedReason(
  reason: FeatureAccessDeniedReason | undefined,
  detail: string | undefined,
  fallback: string,
  organizationName?: string | null,
): string {
  if (detail && detail.trim().length > 0) {
    return sanitizeOrganizationDetail(detail, organizationName || null);
  }
  if (reason === "FEATURE_DISABLED_REGULATORY") {
    return "This feature is currently unavailable pending regulatory phase enablement.";
  }
  if (reason === "FEATURE_DISABLED") {
    return "This feature is not enabled for your organization. Contact your organization administrator to request access.";
  }
  if (reason === "SUBSCRIPTION_REQUIRED" || reason === "SUBSCRIPTION_TIER_INSUFFICIENT") {
    return "Your current subscription does not include this feature.";
  }
  if (reason === "ORG_SUBSCRIPTION_MISSING") {
    return "Your organization does not have an active subscription. Contact your organization administrator.";
  }
  if (reason === "ORG_PLAN_TIER_INSUFFICIENT") {
    return "Your organization's current plan does not include this feature. An administrator can upgrade the plan.";
  }
  if (reason === "ORG_SEAT_REQUIRED" || reason === "ORG_SEAT_LIMIT_REACHED") {
    return "Your organization's seat capacity for this feature is full. Contact your administrator to increase the seat limit.";
  }
  if (reason === "ORG_USER_NOT_ASSIGNED_SEAT") {
    return "You have not been assigned a seat for this feature. Ask your administrator to assign you a seat.";
  }
  return fallback;
}

type ApiErrorPayload = {
  code?: string;
  detail?: string;
  error?: string;
  error_code?: string;
  message?: string;
};

type ApiErrorResponse = {
  status?: number;
  data?: ApiErrorPayload;
};

type ApiErrorShape = {
  response?: ApiErrorResponse;
};

function asApiError(error: unknown): ApiErrorShape | null {
  if (typeof error !== "object" || error === null) {
    return null;
  }

  return error as ApiErrorShape;
}

type StableAccessSnapshot = {
  denied: boolean;
  detail: string;
  reason?: FeatureAccessDeniedReason;
};

const INITIAL_TRANSIENT_RETRY_DELAYS_MS = [200, 600];

async function delay(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

export function usePhaseFeatureAccessGuard(
  options: UsePhaseFeatureAccessGuardOptions,
): UsePhaseFeatureAccessGuardResult {
  const { user, isAuthenticated, loading: authLoading, hasPermission } = useAuth();
  const { isFeatureAvailable, loading: featureFlagsLoading } = useFeatureFlags();
  const organizationName = getUserOrganizationName(user);

  const [accessLoading, setAccessLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessDetail, setAccessDetail] = useState("");
  const [accessReason, setAccessReason] = useState<
    FeatureAccessDeniedReason | undefined
  >(undefined);
  const hasResolvedStableAccessRef = useRef(false);
  const stableAccessSnapshotRef = useRef<StableAccessSnapshot | null>(null);

  const applyStableAccessSnapshot = useCallback(
    (snapshot: StableAccessSnapshot) => {
      hasResolvedStableAccessRef.current = true;
      stableAccessSnapshotRef.current = snapshot;
      setAccessDenied(snapshot.denied);
      setAccessDetail(snapshot.detail);
      setAccessReason(snapshot.reason);
      setAccessLoading(false);
    },
    [],
  );

  const preserveOrReportTransientFailure = useCallback(
    (result: FeatureAvailabilityResult) => {
      const stableSnapshot = stableAccessSnapshotRef.current;
      if (stableSnapshot) {
        setAccessDenied(stableSnapshot.denied);
        setAccessDetail(stableSnapshot.detail);
        setAccessReason(stableSnapshot.reason);
        setAccessLoading(false);
        return;
      }

      setAccessDenied(true);
      setAccessDetail(
        result.detail ||
          `Unable to verify ${options.featureKey
            .toLowerCase()
            .replace(/_/g, " ")} entitlement.`,
      );
      setAccessReason(undefined);
      setAccessLoading(false);
    },
    [options.featureKey],
  );

  const evaluateAccess = useCallback(async () => {
    if (!hasResolvedStableAccessRef.current) {
      setAccessLoading(true);
      setAccessDenied(false);
      setAccessDetail("");
      setAccessReason(undefined);
    }

    if (!isAuthenticated || !user?.id) {
      applyStableAccessSnapshot({
        denied: true,
        detail:
        options.authRequiredDetail || "Authentication is required to access this feature.",
      });
      return;
    }

    if (!user.organization_id) {
      applyStableAccessSnapshot({
        denied: true,
        detail:
        options.missingOrgDetail ||
          "Organization context is required to access this feature.",
      });
      return;
    }

    if (options.permissionCheck) {
      const allowed = await hasPermission(
        options.permissionCheck.resource,
        options.permissionCheck.action,
      );
      if (!allowed) {
        applyStableAccessSnapshot({
          denied: true,
          detail:
          options.permissionCheck.deniedDetail ||
            "You do not have permission to access this feature.",
        });
        return;
      }
    }

    try {
      for (
        let retryIndex = 0;
        retryIndex <= INITIAL_TRANSIENT_RETRY_DELAYS_MS.length;
        retryIndex += 1
      ) {
        const result = await isFeatureAvailable(options.featureKey);
        if (result.available) {
          applyStableAccessSnapshot({
            denied: false,
            detail: "",
          });
          return;
        }

        if (result.transientFailure) {
          if (stableAccessSnapshotRef.current) {
            preserveOrReportTransientFailure(result);
            return;
          }

          if (retryIndex < INITIAL_TRANSIENT_RETRY_DELAYS_MS.length) {
            await delay(INITIAL_TRANSIENT_RETRY_DELAYS_MS[retryIndex]);
            continue;
          }

          preserveOrReportTransientFailure(result);
          return;
        }

        const fallbackDetail =
          result.reason === "FEATURE_DISABLED_REGULATORY"
            ? defaultPhaseDeniedDetail(options.phase, options.featureKey)
            : options.featureUnavailableDetail ||
              defaultFeatureDeniedDetail(options.featureKey);
        applyStableAccessSnapshot({
          denied: true,
          reason: result.reason,
          detail:
          detailFromDeniedReason(
            result.reason,
            result.detail,
            fallbackDetail,
            organizationName,
          ),
        });
        return;
      }
    } catch {
      preserveOrReportTransientFailure({
        available: false,
        transientFailure: true,
      });
    }
  }, [
    applyStableAccessSnapshot,
    hasPermission,
    isAuthenticated,
    isFeatureAvailable,
    options.authRequiredDetail,
    options.featureKey,
    options.featureUnavailableDetail,
    options.missingOrgDetail,
    options.permissionCheck,
    options.phase,
    preserveOrReportTransientFailure,
    organizationName,
    user?.id,
    user?.organization_id,
  ]);

  useEffect(() => {
    if (authLoading || featureFlagsLoading) {
      if (!hasResolvedStableAccessRef.current) {
        setAccessLoading(true);
      }
      return;
    }
    void evaluateAccess();
  }, [authLoading, evaluateAccess, featureFlagsLoading]);

  const handleGuardedApiError = useCallback(
    (error: unknown, fallbackDetail?: string): boolean => {
      const apiError = asApiError(error);
      const status = typeof apiError?.response?.status === "number" ? apiError.response.status : null;

      if (status === 401) {
        applyStableAccessSnapshot({
          denied: true,
          detail:
          options.authRequiredDetail || "Authentication is required to access this feature.",
        });
        return true;
      }

      if (status === 402 || status === 403) {
        const payload = apiError?.response?.data;
        const reason = toFeatureAccessDeniedReason(
          payload?.code || payload?.error || payload?.error_code,
        );
        const detail = detailFromDeniedReason(
          reason,
          payload?.detail || payload?.message || payload?.error,
          fallbackDetail ||
            options.featureUnavailableDetail ||
            defaultFeatureDeniedDetail(options.featureKey),
          organizationName,
        );
        applyStableAccessSnapshot({
          denied: true,
          reason,
          detail,
        });
        return true;
      }

      return false;
    },
    [
      applyStableAccessSnapshot,
      organizationName,
      options.authRequiredDetail,
      options.featureKey,
      options.featureUnavailableDetail,
    ],
  );

  return {
    accessLoading,
    accessDenied,
    accessDetail,
    accessReason,
    refreshAccess: evaluateAccess,
    handleGuardedApiError,
  };
}

export default usePhaseFeatureAccessGuard;
